import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Music, Upload, FileAudio } from 'lucide-react';
import { toast } from 'sonner';
import { audioService } from '@/services/audioService';
import { uploadService } from '@/services/uploadService';
import { useAudioStore } from '@/stores/audioStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { AudioTrack } from '@/types/audio';
import { AdminPageShell, PageHeader, PageError, PageLoading } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';

interface UploadFormData {
  title: string;
  artist: string;
  genre: string;
  mood: string;
  file: File | null;
}

const initialFormData: UploadFormData = {
  title: '',
  artist: '',
  genre: '',
  mood: '',
  file: null,
};

export function AudioCatalog() {
  const { audioTracks, setAudioTracks, isLoading, setLoading, error, setError } = useAudioStore();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState<UploadFormData>(initialFormData);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAudioTracks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await audioService.getAudioTracks();
      setAudioTracks(response.audio_tracks);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar las pistas de audio';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [setAudioTracks, setLoading, setError]);

  useEffect(() => {
    loadAudioTracks();
  }, [loadAudioTracks]);

  const resetForm = () => {
    setFormData(initialFormData);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    resetForm();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('audio/')) {
        toast.error('Por favor selecciona un archivo de audio');
        return;
      }
      setFormData((prev) => {
        const newData = { ...prev, file };
        if (!prev.title) {
          newData.title = file.name.replace(/\.[^/.]+$/, '');
        }
        return newData;
      });
    }
  };

  const handleUpload = async () => {
    if (!formData.file) {
      toast.error('Selecciona un archivo de audio');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('El título es requerido');
      return;
    }
    if (!formData.artist.trim()) {
      toast.error('El artista es requerido');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const signedId = await uploadService.uploadFile(formData.file, (progress) => {
        setUploadProgress(Math.round(progress * 0.5));
      });

      setUploadProgress(50);

      const newTrack = await audioService.createAudioTrack({
        audio_track: {
          title: formData.title.trim(),
          artist: formData.artist.trim(),
          genre: formData.genre.trim() || undefined,
          mood: formData.mood.trim() || undefined,
          file: signedId,
        },
      });

      setAudioTracks([...audioTracks, newTrack]);
      toast.success('Pista subida correctamente');
      handleCloseModal();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al subir la pista');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (track: AudioTrack) => {
    if (track.usage_count > 0) {
      toast.error('No puedes eliminar una pista que está siendo usada por restaurantes');
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar "${track.title}"?`)) return;

    setDeletingId(track.id);
    try {
      await audioService.deleteAudioTrack(track.id);
      setAudioTracks(audioTracks.filter((t) => t.id !== track.id));
      toast.success('Pista eliminada correctamente');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al eliminar la pista de audio');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Catálogo de Audio" />
          <PageLoading variant="list" rows={3} />
        </div>
      </AdminPageShell>
    );
  }

  if (error && !audioTracks.length) {
    return (
      <AdminPageShell>
        <PageError message={error} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Catálogo de Audio"
          subtitle="Gestiona las pistas de música disponibles para los restaurantes"
        >
          <AdminButton variant="primary" onClick={() => setShowUploadModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Subir Pista
          </AdminButton>
        </PageHeader>

        {audioTracks.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Music className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm font-light text-gray-500">
                No hay pistas de audio disponibles
              </p>
              <Button
                variant="outline"
                onClick={() => setShowUploadModal(true)}
                className="mt-4 min-h-11"
              >
                <Plus className="mr-2 h-4 w-4" />
                Subir primera pista
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Título</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Género</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Duración</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Estado</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {audioTracks.map((track) => (
                  <tr key={track.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{track.title}</p>
                        <p className="text-xs font-light text-gray-500">{track.artist}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {track.genre} • {track.mood}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {Math.round(track.duration)}s
                    </td>
                    <td className="px-4 py-3">
                      {track.normalized_at ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                          Normalizada ({track.lufs} LUFS)
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">
                          Pendiente
                        </span>
                      )}
                      {track.usage_count > 0 && (
                        <span className="ml-2 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded">
                          {track.usage_count} restaurante{track.usage_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {track.file_url && (
                          <audio controls src={track.file_url} className="h-8" />
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(track)}
                          disabled={deletingId === track.id || track.usage_count > 0}
                          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={showUploadModal} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Subir Pista de Audio</DialogTitle>
            <DialogDescription>
              Sube un archivo de música para que los restaurantes puedan usarlo en sus videos.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="audio-file">Archivo de Audio *</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  id="audio-file"
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                {formData.file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileAudio className="h-8 w-8 text-gray-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{formData.file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(formData.file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      Haz clic para seleccionar un archivo
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      MP3, WAV, M4A (máx. 50MB)
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nombre de la pista"
                  disabled={isUploading}
                  className="min-h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="artist">Artista *</Label>
                <Input
                  id="artist"
                  value={formData.artist}
                  onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                  placeholder="Nombre del artista"
                  disabled={isUploading}
                  className="min-h-11"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="genre">Género</Label>
                <Input
                  id="genre"
                  value={formData.genre}
                  onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                  placeholder="Ej: Pop, Rock, Jazz"
                  disabled={isUploading}
                  className="min-h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mood">Estado de ánimo</Label>
                <Input
                  id="mood"
                  value={formData.mood}
                  onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
                  placeholder="Ej: Relaxed, Energetic"
                  disabled={isUploading}
                  className="min-h-11"
                />
              </div>
            </div>

            {isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Subiendo...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-900 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={isUploading} className="min-h-11">
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={isUploading} className="min-h-11">
              {isUploading ? 'Subiendo...' : 'Subir Pista'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
